// Event-grouped Upcoming/Past tabs (SearchAndActivityShiftTable-gated). Card membership is frozen for
// the session (like the Waitlists tab): a cancel updates the row in place instead of yanking the card.
import { useMemo, useRef } from 'react';
import { useLocalizedText } from '../../hooks/localizedText';
import { useLocale } from '../../hooks/locale';
import { useSearchParams } from 'react-router-dom';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Alert,
  Box,
  Button,
  Pagination,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import { DetailedSignup, GetDetailedSignupsByQueryParamCommandOutput } from '@amzn/red-velvet-api';
import { externalLinks } from '../../data/externalLinks';
import { useAllPagesRedVelvetQuery } from '../../hooks/allPagesRedVelvetQuery';
import { useUser } from '../../hooks/user';
import {
  ActivityEventGroup,
  ActivityEventSignupData,
  mapSignupDataByEventId,
  partitionActivityEventGroups,
  pinGroupWithShiftFirst,
  restoreCancelledShiftCapacity,
} from '../../utils/volunteerActivityTabUtils';
import { formatEventDateRange } from '../../utils/waitlistsTabUtils';
import { VolunteerEventShiftsTable } from '../shifts/volunteerEventShiftsTable';
import { EventCardHeader } from './components/eventCardHeader';
import { DEFAULT_EVENT_PAGE_SIZE } from './constants';
import { SIGNUP_SURFACE_ACTIVITY } from '../../utils/constants';

const I18N = 'volunteerEventsTab';

// Drain chunk for getDetailedSignupsByQueryParam. Each DetailedSignup embeds a full Event (~3-10KB),
// so 100/page keeps every response well under the ~6MB Lambda response cap while minimizing the
// number of round trips; the full set accumulates client-side.
const DETAILED_SIGNUPS_DRAIN_PAGE_SIZE = 100;

function EventCard({
  group,
  liveData,
  forceCancelShiftId,
}: {
  group: ActivityEventGroup;
  liveData?: ActivityEventSignupData;
  forceCancelShiftId?: string;
}) {
  const { t } = useLocalizedText(['activity', 'translation']);
  const locale = useLocale();
  const { event, shifts } = group;

  // Frozen membership keeps this card after a cancel, but the frozen shift capacity still counts the
  // volunteer's now-cancelled signup, so a just-cancelled full shift would offer Join waitlist. Give
  // the vacated spot back from live participation so it correctly offers Sign up (re-signup).
  const effectiveShifts = useMemo(
    () => restoreCancelledShiftCapacity(shifts, liveData),
    [shifts, liveData]
  );

  const cityState = [event.location?.city, event.location?.province].filter(Boolean).join(', ');
  const meta = [
    formatEventDateRange(event, locale),
    cityState,
    t(`${I18N}.shiftsCount`, { count: shifts.length }),
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <VolunteerEventShiftsTable
      header={<EventCardHeader event={event} meta={meta} />}
      event={event}
      shifts={effectiveShifts}
      // Live per-event signup state (threaded like the Waitlists tab's live entries): when a cancel
      // removes the event's last record the map entry disappears, so fall back to EMPTY — never the
      // frozen group data, which would resurrect the cancelled signup's row state.
      signups={liveData?.signups ?? []}
      attendances={liveData?.attendances ?? []}
      // The Waitlists tab owns waitlist state; these tabs render signup/attendance participation only.
      waitlistEntries={[]}
      forceCancelShiftId={forceCancelShiftId}
      source={SIGNUP_SURFACE_ACTIVITY}
      // pageSize intentionally omitted: cards use the table's standard default (10 shifts/page).
    />
  );
}

// A client-paginated list of event cards; each tab renders its own instance so page state is
// independent (mirrors WaitlistEventCardList).
function EventCardList({
  groups,
  liveByEventId,
  forceCancelShiftId,
}: {
  groups: ActivityEventGroup[];
  liveByEventId: Map<string, ActivityEventSignupData>;
  forceCancelShiftId?: string;
}) {
  const { items, paginationProps } = useCollection(groups, {
    pagination: { pageSize: DEFAULT_EVENT_PAGE_SIZE },
  });

  return (
    <SpaceBetween size="l">
      {items.map((group) => (
        <EventCard
          key={group.event.eventId}
          group={group}
          liveData={liveByEventId.get(group.event.eventId)}
          forceCancelShiftId={forceCancelShiftId}
        />
      ))}
      {paginationProps.pagesCount > 1 && (
        <Box textAlign="center">
          <Pagination {...paginationProps} />
        </Box>
      )}
    </SpaceBetween>
  );
}

export function VolunteerEventsTab({ tab }: { tab: 'upcoming' | 'past' }) {
  const { t } = useLocalizedText(['activity', 'translation']);
  const { userId } = useUser();
  const [searchParams] = useSearchParams();

  // One-click cancel deep link from the signup/auto-signup emails (?shiftId=X&action=cancel): the
  // matching row auto-triggers its cancel mutation (ToggleSignup effect) once its card renders.
  const oneClickCancel = searchParams.get('action')?.toLowerCase() === 'cancel';
  const forceCancelShiftId = oneClickCancel
    ? (searchParams.get('shiftId') ?? undefined)
    : undefined;

  const drainQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: [
      'getDetailedSignupsByQueryParam',
      {
        alias: userId,
        // Parity with the Past tab's flat list: attendance-only records (walk-ins / retroactive
        // tracking) surface alongside signups.
        includeAttendanceOnlyShifts: true,
        pageSize: DETAILED_SIGNUPS_DRAIN_PAGE_SIZE,
      },
    ],
    // Refetch on focus ('always'; the app-wide Infinity staleTime means plain `true` never fires) so
    // an externally-driven status change (waitlist promotion / async auto-signup) refreshes the row
    // on return instead of staying stale until a full reload. One shared drain, so no per-card cost.
    refetchOnWindowFocus: 'always',
    converter: (output: GetDetailedSignupsByQueryParamCommandOutput) => ({
      items: (output.signups || []) as DetailedSignup[],
      next: output.page,
    }),
  });

  const detailedSignups = useMemo(() => drainQuery.data ?? [], [drainQuery.data]);
  const livePartition = useMemo(() => {
    const partition = partitionActivityEventGroups(detailedSignups);
    return {
      upcomingGroups: pinGroupWithShiftFirst(partition.upcomingGroups, forceCancelShiftId),
      pastGroups: partition.pastGroups,
    };
  }, [detailedSignups, forceCancelShiftId]);
  // Live signup/attendance state per event: a cancel flips the row in place while frozen membership
  // (below) keeps the card on screen for the session.
  const liveByEventId = useMemo(() => mapSignupDataByEventId(detailedSignups), [detailedSignups]);

  // Freeze card membership + order on the first data arrival (mirrors the Waitlists tab), so a
  // mid-session cancel neither removes the card nor re-sorts the list under the user.
  const frozenPartitionRef = useRef<typeof livePartition | null>(null);
  if (
    frozenPartitionRef.current === null &&
    (livePartition.upcomingGroups.length > 0 || livePartition.pastGroups.length > 0)
  ) {
    frozenPartitionRef.current = livePartition;
  }
  const { upcomingGroups, pastGroups } = frozenPartitionRef.current ?? livePartition;
  const groups = tab === 'upcoming' ? upcomingGroups : pastGroups;

  if (!drainQuery.isFetched) {
    return (
      <Box textAlign="center" padding="l">
        <StatusIndicator type="loading">{t('signups.loading')}</StatusIndicator>
      </Box>
    );
  }

  // Surface the hard error only on a true initial-load failure, i.e. the drain has never produced
  // data. React Query keeps the last good data on a failed refetch, so a focus-refetch blip must not
  // flip a user into a full-tab error: one with cards keeps them (frozen membership), and an
  // empty-but-valid user keeps their empty state (a successful empty load leaves `data` defined).
  if (drainQuery.error && drainQuery.data === undefined) {
    return <Alert type="error" header={t(`${I18N}.loadingError`)} />;
  }

  if (groups.length === 0) {
    return (
      <Box textAlign="center" padding="l">
        <SpaceBetween direction="vertical" size="s">
          <b className="pseudoHeader">{t('signups.noActivities')}</b>
          <div>{t('signups.noActivitiesSubtitle')}</div>
          <Button
            variant="primary"
            data-aci-analytics-name="myActivity_search"
            href={externalLinks.myActivity.search}
          >
            {t('signups.exploreActivities')}
          </Button>
        </SpaceBetween>
      </Box>
    );
  }

  return (
    <EventCardList
      groups={groups}
      liveByEventId={liveByEventId}
      forceCancelShiftId={forceCancelShiftId}
    />
  );
}
